// Compare with market: pick one of our apps, put it into the snapshot as
// "ours" (collected, analysed and keyword-fetched like the rest, never
// counted as market), and read the gaps — what the sample shares that we lack.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addCompareApp, compareCandidates, getGaps, getRun, type KeywordGap } from '@/api/aso';
import { Button } from '@/components/Button';
import { Select } from '@/components/Select';
import { useCanEdit } from '@/components/EditOnly';
import { useToastStore } from '@/store/toast';

const pct = (a: number, b: number) => (b ? `${Math.round((a / b) * 100)}%` : '—');

export function CompareTab({ runId }: { runId: number }) {
  const canEdit = useCanEdit();
  const qc = useQueryClient();
  const toast = useToastStore((s) => s.push);
  const candQ = useQuery({ queryKey: ['aso-compare-candidates', runId], queryFn: () => compareCandidates(runId), enabled: runId > 0 });
  const runQ = useQuery({ queryKey: ['aso-run', runId], queryFn: () => getRun(runId), enabled: runId > 0 });
  const ours = useMemo(() => (runQ.data?.apps ?? []).filter((a) => a.role === 'ours' || a.is_ours === 1), [runQ.data]);
  const [picked, setPicked] = useState<number | null>(null);
  const [pkg, setPkg] = useState('');
  const appId = picked ?? ours[0]?.app_id ?? null;

  const add = useMutation({
    mutationFn: (p: string) => addCompareApp(runId, p),
    onSuccess: (r) => {
      toast(r.already ? 'Already in this snapshot' : 'Added — collecting its listing, then analysing');
      qc.invalidateQueries({ queryKey: ['aso-run', runId] });
      qc.invalidateQueries({ queryKey: ['aso-compare-candidates', runId] });
      setPicked(r.app_id);
    },
    onError: (e: any) => toast(e?.message ?? 'Failed to add the app'),
  });

  const gapsQ = useQuery({
    queryKey: ['aso-gaps', runId, appId],
    queryFn: () => getGaps(runId, appId!),
    enabled: !!appId,
    refetchInterval: (q) => ((q.state.data?.pending ?? 0) > 0 ? 15_000 : false),
  });
  const g = gapsQ.data;
  const labels = g?.labels;

  const candidates = (candQ.data ?? []).map((c) => ({ value: c.bundle_id, label: `${c.name} · ${c.bundle_id}${c.aso_app_id ? ' (in snapshot)' : ''}` }));

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
        <div className="sm:w-80">
          <label className="label">Our app</label>
          <Select value={pkg} options={candidates} onChange={setPkg} placeholder="Pick one of our Android apps" searchable className="mt-1" />
        </div>
        <input className="input sm:w-72" placeholder="…or any package name" value={pkg} onChange={(e) => setPkg(e.target.value.trim())} />
        {canEdit && <Button disabled={!pkg || add.isPending} onClick={() => add.mutate(pkg)}>Compare</Button>}
        {ours.length > 1 && (
          <Select value={String(appId ?? '')} options={ours.map((o) => ({ value: String(o.app_id), label: o.name ?? o.store_id }))} onChange={(v) => setPicked(Number(v))} className="sm:w-64" />
        )}
      </div>

      {!appId && <div className="p-6 text-center text-content-subtle bg-surface border border-border rounded-lg">Pick an app and press Compare. Its listing is collected into this snapshot and measured against the market.</div>}

      {g && (
        <>
          <div className="text-xs text-content-muted">
            {g.pending > 0 && <span className="text-primary">Still working: {g.pending} task(s) left — the gaps fill in as they finish. </span>}
            {!g.our_app.collected && g.pending === 0 && <span className="text-warning">Our listing could not be collected in this market. </span>}
            Market: {g.apps_total} apps.
          </div>

          <Section title="Keyword gaps" hint="Keywords the sample ranks for. 'Ours' shows our rank from the provider and whether the word is in our own copy.">
            <KeywordGapTable rows={g.keywords} labels={labels?.cluster ?? {}} />
          </Section>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Section title="Phrases the market uses" hint="Title / short phrases shared by the sample, and whether our copy has them.">
              <SimpleGapList rows={g.phrases.map((p) => ({ key: `${p.placement}:${p.phrase}`, label: `${p.phrase} (${p.placement})`, n: p.apps_count, total: p.apps_total, we: p.we_use }))} />
            </Section>
            <Section title="Screenshot hooks" hint="What competitors' screenshots promise. 'We' = any of our screenshots carries it.">
              <SimpleGapList rows={g.hooks.map((h) => ({ key: h.slug, label: labels?.hook[h.slug] ?? h.slug, n: h.apps_count, total: h.apps_total, we: h.we_use }))} />
            </Section>
            <Section title="Icon patterns" hint="Visual tags on competitors' icons vs ours.">
              <SimpleGapList rows={g.icon_tags.map((t) => ({ key: t.slug, label: labels?.tag[t.slug] ?? t.slug, n: t.apps_count, total: t.apps_total, we: t.we_use }))} />
            </Section>
          </div>
        </>
      )}
    </div>
  );
}

function Section({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="label">{title}</div>
      <p className="text-[11px] text-content-subtle mb-2">{hint}</p>
      {children}
    </div>
  );
}

function SimpleGapList({ rows }: { rows: { key: string; label: string; n: number; total: number; we: boolean }[] }) {
  return (
    <div className="bg-surface border border-border rounded-lg divide-y divide-border max-h-96 overflow-y-auto">
      {rows.length === 0 && <div className="p-4 text-xs text-content-subtle">Nothing yet.</div>}
      {rows.map((r) => (
        <div key={r.key} className="flex items-center gap-2 px-3 py-1.5 text-sm">
          <span className={`w-2 h-2 rounded-full shrink-0 ${r.we ? 'bg-success' : 'bg-danger'}`} title={r.we ? 'we use it' : 'we do not'} />
          <span className="text-content truncate flex-1">{r.label}</span>
          <span className="text-xs text-content-muted whitespace-nowrap">{r.n}/{r.total} · {pct(r.n, r.total)}</span>
        </div>
      ))}
    </div>
  );
}

function KeywordGapTable({ rows, labels }: { rows: KeywordGap[]; labels: Record<string, string> }) {
  const [onlyGaps, setOnlyGaps] = useState(true);
  const list = onlyGaps ? rows.filter((k) => k.our_rank === null && !k.in_title && !k.in_short) : rows;
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-xs text-content-muted">
        <input type="checkbox" checked={onlyGaps} onChange={(e) => setOnlyGaps(e.target.checked)} /> Only keywords we neither rank for nor mention in title / short
      </label>
      <div className="bg-surface border border-border rounded-lg overflow-x-auto max-h-[32rem] overflow-y-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-surface-muted text-xs uppercase tracking-wide text-content-subtle sticky top-0">
            <tr>
              <th className="text-left p-2 font-medium">Keyword</th>
              <th className="text-left p-2 font-medium">Cluster</th>
              <th className="text-right p-2 font-medium">Traffic</th>
              <th className="text-right p-2 font-medium">Competitors ranking</th>
              <th className="text-right p-2 font-medium">In competitors' titles</th>
              <th className="text-right p-2 font-medium">Our rank</th>
              <th className="text-left p-2 font-medium">In our copy</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-xs text-content-subtle">No keyword data yet — collect keywords for this snapshot first.</td></tr>}
            {list.slice(0, 300).map((k) => (
              <tr key={k.keyword} className="border-t border-border">
                <td className="p-2 text-content font-medium">{k.keyword}</td>
                <td className="p-2 text-xs text-content-muted">{k.cluster_slug ? (labels[k.cluster_slug] ?? k.cluster_slug) : '—'}</td>
                <td className="p-2 text-right text-content">{k.traffic ?? '—'}</td>
                <td className="p-2 text-right text-content">{k.competitors_ranking}{k.best_rank ? <span className="text-content-subtle"> (best #{k.best_rank})</span> : null}</td>
                <td className="p-2 text-right text-content-muted">{k.competitors_in_title}</td>
                <td className={`p-2 text-right ${k.our_rank === null ? 'text-danger' : 'text-content'}`}>{k.our_rank === null ? 'no' : `#${k.our_rank}`}</td>
                <td className="p-2 text-xs">
                  {[k.in_title && 'title', k.in_short && 'short', k.in_full && 'full'].filter(Boolean).join(', ') || <span className="text-danger">no</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
